import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import { LiEventInclusionStatus, LiEventScrapeStatus } from "@/enum/scrape-enum";

import { PeopleResponseSchema } from "../../pages/Apollo/schema/get-people";
import {
	CreateLiEvent,
	LiEvent,
	LiEventResponse,
	LiEventResponseSchema,
	LiEventSchema,
	ValidateEventUrlResponse,
	ValidateEventUrlResponseSchema,
} from "../../pages/LiEvent/schema/liEvent";
import {
	LiEventEntry,
	LiEventEntrySchema,
	LiEventEntrysResponse,
	LiEventEntrysResponseSchema,
	LiEventEntrysWithCountResponse,
	LiEventEntrysWithCountResponseSchema,
} from "../../pages/LiEvent/schema/liEventEntry";
import { LiEventEntryAttendeeResponse } from "../../pages/LiEvent/schema/liEventEntryAttendee";

export const liEventApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getLiEvents: builder.query<LiEventResponse, RequestPaginationParams>({
			query: (params) => ({
				url: "liEvent",
				params: {
					...(params && { ...params }),
				},
				responseSchema: LiEventResponseSchema,
			}),
			providesTags: ["LiEvent"],
		}),
		getLiEventForScrape: builder.query<
			LiEventEntrysResponse,
			RequestPaginationParams & { id: number } & { filter: LiEventScrapeStatus[] }
		>({
			query: (params) => ({
				url: `liEvent/${params.id}/events`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: LiEventEntrysResponseSchema,
			}),
			providesTags: ["LiEvent"],
		}),
		getLiEventEntriesByListId: builder.query<
			LiEventEntrysWithCountResponse,
			RequestPaginationParams & { id: number } & { inclusionStatus: LiEventInclusionStatus }
		>({
			query: (params) => ({
				url: `liEvent/${params.id}/byListId`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: LiEventEntrysWithCountResponseSchema,
			}),
			providesTags: (result, error, arg) => [{ type: "LiEventEntries", id: arg.id }],
		}),
		updateLiEventEntry: builder.mutation<Partial<LiEventEntry>, Partial<LiEventEntry>>({
			query: ({ id, ...body }) => ({
				url: `liEvent/event/${id}`,
				method: "PATCH",
				body,
				responseSchema: LiEventEntrySchema,
			}),
			invalidatesTags: ["LiEvent"],
		}),
		updateLiEventEntries: builder.mutation<
			LiEventEntrysResponse,
			{ status: LiEventInclusionStatus; eventEntryIds: number[]; listId: number }
		>({
			query: (body) => ({
				url: `liEvent/eventEntries`,
				method: "PATCH",
				body,
				responseSchema: LiEventEntrysResponseSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "LiEventEntries", id: arg.listId }];
				}

				return ["LiEventEntries"];
			},
		}),
		getLiEventAttendees: builder.query<
			LiEventEntryAttendeeResponse,
			RequestPaginationParams & { id: number }
		>({
			query: (params) => ({
				url: `liEvent/${params.id}/attendees`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: PeopleResponseSchema,
			}),
			providesTags: ["People"],
		}),
		getLiEvent: builder.query<LiEvent, number>({
			query: (id: number) => ({
				url: `liEvent/${id}`,
				params: {
					id,
				},
				responseSchema: LiEventSchema,
			}),
			providesTags: ["LiEvent"],
		}),
		// getLiEventById: builder.query<SavedLiEventResponse, number>({
		// 	query: (id) => ({
		// 		url: `liEvent/${id}`,
		// 		responseSchema: SavedLiEventSchema,
		// 	}),
		// 	providesTags: [{ type: "LiEvents", id: "DETAIL" }],
		// }),
		triggerLiEvent: builder.mutation<void, number>({
			query: (id) => ({
				url: `liEvent/trigger/${id}`,
				method: "POST",
			}),
			invalidatesTags: ["LiEvent"],
		}),
		createLiEvent: builder.mutation<Partial<LiEvent>, CreateLiEvent>({
			query: (body) => ({
				url: `liEvent`,
				method: "POST",
				body,
				responseSchema: LiEventSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "LiEvent"];
				}

				return ["LiEvent"];
			},
		}),
		updateLiEvent: builder.mutation<Partial<LiEvent>, Partial<LiEvent>>({
			query: ({ id, ...body }) => ({
				url: `liEvent/${id}`,
				method: "PATCH",
				body,
				responseSchema: LiEventSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "LiEvent"];
				}

				return ["LiEvent"];
			},
		}),
		toggleLiEvent: builder.mutation<Partial<LiEvent>, Partial<LiEvent>>({
			query: ({ id, ...body }) => ({
				url: `liEvent/toggle/${id}`,
				method: "PATCH",
				body,
				responseSchema: LiEventSchema,
			}),
			invalidatesTags: ["LiEvent"],
		}),
		validateEventUrl: builder.mutation<ValidateEventUrlResponse, { url: string }>({
			query: (body) => ({
				url: `liEvent/validate-url`,
				method: "POST",
				body,
				responseSchema: ValidateEventUrlResponseSchema,
			}),
		}),
		deleteLiEvent: builder.mutation<Partial<LiEvent>, { id: number; listId?: number }>({
			query: ({ id, listId }) => ({
				url: listId ? `liEvent/${id}/list/${listId}` : `liEvent/${id}`,
				method: "DELETE",
				responseSchema: LiEventSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "LiEvent"];
				}

				return ["LiEvent"];
			},
		}),
	}),
});

export const {
	useGetLiEventsQuery,
	useLazyGetLiEventsQuery,
	useGetLiEventQuery,
	useGetLiEventForScrapeQuery,
	useLazyGetLiEventForScrapeQuery,
	useGetLiEventAttendeesQuery,
	useUpdateLiEventMutation,
	useToggleLiEventMutation,
	useDeleteLiEventMutation,
	useTriggerLiEventMutation,
	useCreateLiEventMutation,
	useUpdateLiEventEntryMutation,
	useGetLiEventEntriesByListIdQuery,
	useUpdateLiEventEntriesMutation,
	useValidateEventUrlMutation,
} = liEventApi;
export const accountApiReducer = liEventApi.reducer;
export const accountApiMiddleware = liEventApi.middleware;
